import { useEffect, useRef, useState } from "react";

import { fetchAiAccess } from "../aiApi";
import AiActionFields from "./AiActionFields";
import AiActionProposal from "./AiActionProposal";
import { actionLineArrayFits } from "./actionLimits";
import {
  confirmActionDraft,
  createActionDraft,
  fetchActionDraft,
  updateActionDraft,
} from "./aiActionApi";

const actionKey = (prefix) => {
  const uuid = globalThis.crypto?.randomUUID?.();
  return uuid
    ? `${prefix}-${uuid}`
    : `${prefix}-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}-${Math.random().toString(36).slice(2)}`;
};

const quotaText = (quota) => {
  if (!quota) return "Cota indisponível";
  if (quota.unlimited) return "Uso ilimitado";
  if (quota.remaining === 0) return "Cota utilizada neste período";
  return quota.remaining === 1
    ? "1 uso disponível"
    : `${quota.remaining} usos disponíveis`;
};

const staleError = {
  status: 409,
  code: "ai_action_stale",
  errorCode: "ai_action_stale",
  message:
    "A proposta foi atualizada porque os dados financeiros mudaram. Revise os novos valores e confirme novamente.",
};
const NOOP = () => {};
const editableValues = (draft) => {
  const fields = draft?.fields ?? {};
  return {
    ...fields,
    ...(Array.isArray(fields.selectedCandidateRefs)
      ? { selectedCandidateRefs: [...fields.selectedCandidateRefs] }
      : {}),
    ...(Array.isArray(fields.originLines)
      ? {
          originLines: fields.originLines.map((line) => ({
            candidateRef: line.candidateRef,
            fromCompetence: line.fromCompetence,
            amountCents: line.amountCents,
          })),
        }
      : {}),
    ...(Array.isArray(fields.allocationLines)
      ? {
          allocationLines: fields.allocationLines.map((line) => ({
            candidateRef: line.candidateRef,
            amountCents: line.amountCents,
          })),
        }
      : {}),
  };
};

const allocationBatchIsBalanced = (values, module) => {
  const totalCents = module === "advancement" ? values?.amountCents : values?.totalCents;
  const lines = values?.allocationLines;
  const selectedRefs = Array.isArray(values?.selectedCandidateRefs)
    ? values.selectedCandidateRefs
    : [];
  if (
    !Number.isSafeInteger(totalCents)
    || totalCents < 1
    || !Array.isArray(lines)
    || lines.length < 1
    || lines.length > 20
  ) return false;
  const refs = lines.map((line) => line?.candidateRef);
  if (
    new Set(refs).size !== refs.length
    || new Set(selectedRefs).size !== selectedRefs.length
    || refs.length !== selectedRefs.length
    || selectedRefs.some((ref) => !refs.includes(ref))
  ) return false;
  const allocated = lines.reduce((total, line) => (
    Number.isSafeInteger(total)
    && Number.isSafeInteger(line?.amountCents)
    && line.amountCents > 0
      ? total + line.amountCents
      : Number.NaN
  ), 0);
  return Number.isSafeInteger(allocated) && allocated === totalCents;
};

const originBatchIsBalanced = (values) => {
  if (
    !Number.isSafeInteger(values?.amountCents) ||
    values.amountCents < 1 ||
    !actionLineArrayFits(values.originLines) ||
    values.originLines.some(
      (line) =>
        !Number.isSafeInteger(line?.amountCents) || line.amountCents < 1,
    )
  )
    return false;
  const totalCents = values.originLines.reduce(
    (total, line) => total + line.amountCents,
    0,
  );
  return Number.isSafeInteger(totalCents) && totalCents === values.amountCents;
};

const AiActionComposer = ({ workId, module, onExecuted = NOOP }) => {
  const accessControllerRef = useRef(null);
  const operationControllerRef = useRef(null);
  const patchTimerRef = useRef(null);
  const pendingPatchRef = useRef({});
  const createKeyRef = useRef(null);
  const confirmKeyRef = useRef(null);
  const restoreFocusRef = useRef(null);
  const [access, setAccess] = useState(null);
  const [quota, setQuota] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [draft, setDraft] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [outcome, setOutcome] = useState(null);
  const [outcomeReason, setOutcomeReason] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState(null);
  const [requiresReview, setRequiresReview] = useState(false);
  const [pendingEdits, setPendingEdits] = useState(false);
  const [editFailure, setEditFailure] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    accessControllerRef.current?.abort();
    operationControllerRef.current?.abort();
    clearTimeout(patchTimerRef.current);
    patchTimerRef.current = null;
    pendingPatchRef.current = {};
    operationControllerRef.current = null;
    createKeyRef.current = null;
    confirmKeyRef.current = null;
    restoreFocusRef.current = null;
    setAccess(null);
    setQuota(null);
    setPrompt("");
    setDraft(null);
    setEditValues({});
    setOutcome(null);
    setOutcomeReason(null);
    setError(null);
    setRequiresReview(false);
    setPendingEdits(false);
    setEditFailure(false);
    setPhase("loading");

    const controller = new AbortController();
    accessControllerRef.current = controller;
    let active = true;
    fetchAiAccess(workId, { signal: controller.signal })
      .then((payload) => {
        if (!active) return;
        setAccess(payload);
        setQuota(payload.quota ?? null);
        setPhase("idle");
      })
      .catch((caught) => {
        if (!active || caught?.code === "request_cancelled") return;
        setError(caught);
        setPhase("error");
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [workId, module, reloadKey]);

  useEffect(
    () => () => {
      clearTimeout(patchTimerRef.current);
      operationControllerRef.current?.abort();
    },
    [],
  );

  const exhausted = quota?.unlimited === false && quota?.remaining === 0;
  const networkBusy =
    phase === "creating" || phase === "updating" || phase === "confirming";
  const busy = networkBusy || pendingEdits;
  const accessReady = phase !== "loading" && access != null;
  const requestBaseAllowed =
    accessReady && access?.enabled === true && !busy && draft == null;
  const canCheckProcessing =
    requestBaseAllowed &&
    outcome === "processing" &&
    Boolean(prompt.trim()) &&
    !operationControllerRef.current;
  const canStartNew =
    requestBaseAllowed && outcome !== "processing" && !exhausted;
  const canCreateNew =
    canStartNew && Boolean(prompt.trim()) && !operationControllerRef.current;
  const canSubmitRequest = canCheckProcessing || canCreateNew;
  const promptLocked = !canStartNew || outcome === "processing";
  const hasUnbalancedOriginEdits =
    module === "advancement" &&
    pendingEdits &&
    !originBatchIsBalanced(editValues);
  const hasUnbalancedAllocationEdits =
    pendingEdits &&
    Object.hasOwn(editValues, "allocationLines") &&
    !allocationBatchIsBalanced(editValues, module);
  const originLinesNeedReview =
    draft?.module === "advancement" &&
    draft?.state === "needs_input" &&
    draft?.missingFields?.includes("originLines");
  const allocationLinesNeedReview =
    draft?.state === "needs_input" &&
    draft?.missingFields?.includes("allocationLines");
  const proposalLinesNeedReview = originLinesNeedReview || allocationLinesNeedReview;

  useEffect(() => {
    if (networkBusy || !restoreFocusRef.current) return;
    const control = restoreFocusRef.current;
    restoreFocusRef.current = null;
    if (control.isConnected) control.focus();
  }, [networkBusy, draft?.state]);

  const createDraft = async () => {
    if (!canSubmitRequest) return;
    const controller = new AbortController();
    operationControllerRef.current = controller;
    createKeyRef.current ??= actionKey("create");
    setPhase("creating");
    setError(null);
    setOutcome(null);
    setOutcomeReason(null);
    try {
      const result = await createActionDraft(
        workId,
        {
          module,
          prompt: prompt.trim(),
        },
        {
          idempotencyKey: createKeyRef.current,
          signal: controller.signal,
        },
      );
      if (result?.quota) setQuota(result.quota);
      if (result?.draft) {
        setDraft(result.draft);
        setEditValues(editableValues(result.draft));
        setOutcome(result.draft.state);
        setOutcomeReason(null);
      } else {
        setOutcome(result?.status ?? "cannot_propose");
        setOutcomeReason(result?.reason ?? null);
      }
      setPhase("idle");
    } catch (caught) {
      if (caught?.code !== "request_cancelled") {
        if (
          caught?.errorCode === "ai_provider_unavailable" ||
          caught?.errorCode === "ai_provider_invalid_output"
        ) {
          createKeyRef.current = null;
        }
        setError(caught);
        setPhase("error");
      }
    } finally {
      if (operationControllerRef.current === controller)
        operationControllerRef.current = null;
    }
  };

  const adoptConflictDraft = (caught) => {
    if (!caught?.draft) return false;
    clearTimeout(patchTimerRef.current);
    patchTimerRef.current = null;
    pendingPatchRef.current = {};
    setDraft(caught.draft);
    setEditValues(editableValues(caught.draft));
    setOutcome(caught.draft.state);
    setRequiresReview(true);
    setPendingEdits(false);
    setEditFailure(false);
    setError(staleError);
    return true;
  };

  const recoverConflictDraft = async (caught, controller) => {
    if (adoptConflictDraft(caught)) return true;
    const errorCode = caught?.errorCode ?? caught?.code;
    if (errorCode !== "ai_action_version_conflict" || !draft?.draftId)
      return false;
    try {
      const current = await fetchActionDraft(workId, draft.draftId, {
        signal: controller.signal,
      });
      return adoptConflictDraft({ ...caught, draft: current?.draft });
    } catch (refreshError) {
      if (refreshError?.code !== "request_cancelled") setError(refreshError);
      return false;
    }
  };

  const patchDraft = async (changes) => {
    if (
      !draft ||
      (draft.state !== "needs_input" && draft.state !== "ready_for_review") ||
      networkBusy ||
      operationControllerRef.current
    )
      return;
    const controller = new AbortController();
    operationControllerRef.current = controller;
    restoreFocusRef.current =
      typeof document !== "undefined" &&
      typeof HTMLElement !== "undefined" &&
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    setPhase("updating");
    setError(null);
    try {
      const result = await updateActionDraft(
        workId,
        draft.draftId,
        {
          expectedVersion: draft.version,
          ...changes,
        },
        { signal: controller.signal },
      );
      pendingPatchRef.current = {};
      setDraft(result.draft);
      setEditValues(editableValues(result.draft));
      setOutcome(result.draft.state);
      setRequiresReview(false);
      setPendingEdits(false);
      setEditFailure(false);
      setPhase("idle");
    } catch (caught) {
      if (caught?.code === "request_cancelled") return;
      if (await recoverConflictDraft(caught, controller)) {
        if (operationControllerRef.current === controller)
          operationControllerRef.current = null;
        setPhase("idle");
      } else if (
        caught?.errorCode === "ai_action_expired" ||
        caught?.code === "ai_action_expired"
      ) {
        pendingPatchRef.current = {};
        setDraft((current) => ({ ...current, state: "expired" }));
        setOutcome("expired");
        setPendingEdits(false);
        setEditFailure(false);
        setError(null);
        setPhase("idle");
      } else {
        setError(caught);
        setPendingEdits(true);
        setEditFailure(true);
        setPhase("error");
      }
    } finally {
      if (operationControllerRef.current === controller)
        operationControllerRef.current = null;
    }
  };

  const submitPatch = (changes) => {
    pendingPatchRef.current = { ...pendingPatchRef.current, ...changes };
    const nextEditValues = { ...editValues, ...changes };
    setEditValues(nextEditValues);
    setPendingEdits(true);
    setEditFailure(false);
    setError(null);
    clearTimeout(patchTimerRef.current);
    if (
      module === "advancement" &&
      Object.prototype.hasOwnProperty.call(changes, "originLines") &&
      !originBatchIsBalanced(nextEditValues)
    ) {
      patchTimerRef.current = null;
      return;
    }
    if (
      Object.prototype.hasOwnProperty.call(changes, "allocationLines") &&
      !allocationBatchIsBalanced(nextEditValues, module)
    ) {
      patchTimerRef.current = null;
      return;
    }
    patchTimerRef.current = setTimeout(async () => {
      const nextChanges = { ...pendingPatchRef.current };
      patchTimerRef.current = null;
      await patchDraft(nextChanges);
    }, 300);
  };

  const retryPendingChanges = () => {
    if (!pendingEdits || networkBusy || operationControllerRef.current) return;
    clearTimeout(patchTimerRef.current);
    patchTimerRef.current = null;
    setEditFailure(false);
    setError(null);
    patchDraft({ ...pendingPatchRef.current });
  };

  const discardPendingChanges = () => {
    clearTimeout(patchTimerRef.current);
    patchTimerRef.current = null;
    pendingPatchRef.current = {};
    setEditValues(editableValues(draft));
    setPendingEdits(false);
    setEditFailure(false);
    setError(null);
    setPhase("idle");
  };

  const recalculateOrigins = () => {
    const amountCents = draft?.fields?.amountCents;
    if (
      !draft ||
      draft.module !== "advancement" ||
      !Number.isSafeInteger(amountCents) ||
      amountCents < 1 ||
      networkBusy ||
      operationControllerRef.current
    )
      return;
    clearTimeout(patchTimerRef.current);
    patchTimerRef.current = null;
    const changes = { amountCents };
    pendingPatchRef.current = changes;
    setPendingEdits(true);
    setEditFailure(false);
    setError(null);
    patchDraft(changes);
  };

  const confirmDraft = async () => {
    if (
      !draft ||
      draft.state !== "ready_for_review" ||
      requiresReview ||
      busy ||
      operationControllerRef.current
    )
      return;
    const controller = new AbortController();
    operationControllerRef.current = controller;
    confirmKeyRef.current ??= actionKey("confirm");
    setPhase("confirming");
    setError(null);
    try {
      const result = await confirmActionDraft(
        workId,
        draft.draftId,
        {
          expectedVersion: draft.version,
        },
        {
          idempotencyKey: confirmKeyRef.current,
          signal: controller.signal,
        },
      );
      const executedDraft = result.draft ?? {
        ...draft,
        state: "executed",
        execution: result.execution,
      };
      setDraft(executedDraft);
      setEditValues(editableValues(executedDraft));
      setOutcome("executed");
      setPhase("idle");
      onExecuted(result.execution ?? executedDraft.execution);
    } catch (caught) {
      if (caught?.code === "request_cancelled") return;
      if (await recoverConflictDraft(caught, controller)) {
        if (operationControllerRef.current === controller)
          operationControllerRef.current = null;
        setPhase("idle");
      } else if (
        caught?.errorCode === "ai_action_expired" ||
        caught?.code === "ai_action_expired"
      ) {
        pendingPatchRef.current = {};
        setDraft((current) => ({ ...current, state: "expired" }));
        setOutcome("expired");
        setPendingEdits(false);
        setEditFailure(false);
        setError(null);
        setPhase("idle");
      } else {
        setError(caught);
        setPhase("error");
      }
    } finally {
      if (operationControllerRef.current === controller)
        operationControllerRef.current = null;
    }
  };

  const resetProposal = () => {
    operationControllerRef.current?.abort();
    clearTimeout(patchTimerRef.current);
    patchTimerRef.current = null;
    pendingPatchRef.current = {};
    operationControllerRef.current = null;
    createKeyRef.current = null;
    confirmKeyRef.current = null;
    restoreFocusRef.current = null;
    setDraft(null);
    setEditValues({});
    setOutcome(null);
    setOutcomeReason(null);
    setError(null);
    setRequiresReview(false);
    setPendingEdits(false);
    setEditFailure(false);
    setPrompt("");
    setPhase("idle");
  };

  return (
    <section
      className="ai-action-composer"
      aria-label="Criar ação financeira com IA"
    >
      <header className="ai-action-composer__header">
        <div>
          <span>Ampli Obras com IA</span>
          <h3>
            {module === "extra_value"
              ? "Planejar estouro ou economia"
              : "Planejar antecipação"}
          </h3>
        </div>
        <p>
          Descreva a mudança. O Ampli monta uma proposta para sua revisão antes
          de qualquer execução.
        </p>
      </header>

      {phase === "loading" ? (
        <p className="ai-action-composer__status" role="status">
          Verificando acesso e cota compartilhada…
        </p>
      ) : null}

      {access ? (
        <div
          className={`ai-action-composer__quota${
            exhausted ? " ai-action-composer__quota--exhausted" : ""
          }`}
        >
          <strong>{quotaText(quota)}</strong>
          <span>
            A cota é compartilhada por todos os usuários desta obra e por todos
            os usos da IA.
          </span>
        </div>
      ) : null}

      {access && !access.enabled ? (
        <p className="ai-action-composer__disabled" role="status">
          <strong>IA não habilitada para esta obra</strong>
          <span>O acesso pode ser liberado no Backoffice.</span>
        </p>
      ) : null}

      <form
        className="ai-action-composer__request"
        onSubmit={(event) => {
          event.preventDefault();
          createDraft();
        }}
      >
        <label>
          <span>O que você quer fazer?</span>
          <textarea
            value={prompt}
            maxLength={500}
            disabled={promptLocked}
            placeholder={
              module === "extra_value"
                ? "Ex.: registre uma economia de R$ 20 mil em estrutura a partir de outubro"
                : "Ex.: antecipe R$ 300 mil de aço para outubro"
            }
            onChange={(event) => {
              setPrompt(event.target.value);
              if (!draft) createKeyRef.current = null;
            }}
          />
        </label>
        <button type="submit" disabled={!canSubmitRequest}>
          {phase === "creating"
            ? "Montando…"
            : outcome === "processing"
            ? "Verificar proposta"
            : "Montar proposta"}
        </button>
      </form>

      {phase === "creating" ? (
        <p className="ai-action-composer__status" role="status">
          Interpretando o pedido e calculando a proposta…
        </p>
      ) : null}
      {phase === "updating" ? (
        <p className="ai-action-composer__status" role="status">
          Recalculando a proposta sem novo uso da IA…
        </p>
      ) : null}
      {pendingEdits && phase === "idle" ? (
        <p className="ai-action-composer__status" role="status">
          Preparando as alterações para recalcular a proposta…
        </p>
      ) : null}
      {phase === "confirming" ? (
        <p className="ai-action-composer__status" role="status">
          Executando a ação financeira…
        </p>
      ) : null}
      {outcome === "processing" && phase !== "creating" ? (
        <p className="ai-action-composer__status" role="status">
          A proposta ainda está sendo preparada. Verifique novamente em
          instantes com o mesmo pedido.
        </p>
      ) : null}

      {outcome === "cannot_propose" ? (
        <div className="ai-action-composer__empty">
          <p role="status">
            {outcomeReason === "no_candidates"
              ? "Não encontrei itens elegíveis para montar esta proposta. Tente descrever outro item da EAP."
              : "Não foi possível montar uma proposta segura com este pedido."}
          </p>
          <button type="button" onClick={resetProposal}>
            Tentar outro pedido
          </button>
        </div>
      ) : null}

      {draft?.state === "needs_input" && !proposalLinesNeedReview ? (
        <div className="ai-action-composer__draft">
          {draft.explanation ? <p>{draft.explanation}</p> : null}
          <AiActionFields
            draft={draft}
            values={editValues}
            disabled={networkBusy}
            onChange={(field, value) => submitPatch({ [field]: value })}
          />
        </div>
      ) : null}

      {draft &&
      (draft.state === "ready_for_review" ||
        proposalLinesNeedReview ||
        draft.state === "executed" ||
        draft.state === "expired") ? (
        <AiActionProposal
          draft={draft}
          workId={workId}
          editValues={editValues}
          busy={networkBusy}
          confirmationBlocked={pendingEdits}
          confirming={phase === "confirming"}
          requiresReview={requiresReview}
          onPatch={submitPatch}
          onConfirm={confirmDraft}
          onRecalculateOrigins={recalculateOrigins}
          onReviewed={() => {
            setRequiresReview(false);
            setError(null);
          }}
        />
      ) : null}
      {editFailure || hasUnbalancedOriginEdits || hasUnbalancedAllocationEdits ? (
        <div
          className="ai-action-composer__edit-recovery"
          role="group"
          aria-label="Alterações não salvas"
        >
          {editFailure ? (
            <button
              type="button"
              onClick={retryPendingChanges}
              disabled={networkBusy}
            >
              Tentar salvar alterações
            </button>
          ) : null}
          <button
            type="button"
            onClick={discardPendingChanges}
            disabled={networkBusy}
          >
            Descartar alterações
          </button>
        </div>
      ) : null}
      {draft?.state === "expired" ? (
        <button
          type="button"
          className="ai-action-composer__reset"
          onClick={resetProposal}
        >
          Montar nova proposta
        </button>
      ) : null}

      {error ? (
        <div className="ai-action-composer__error" role="alert">
          <span>{error.message}</span>
          {!access ? (
            <button
              type="button"
              onClick={() => setReloadKey((value) => value + 1)}
            >
              Tentar carregar novamente
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};

export { quotaText };
export default AiActionComposer;
